import { __assign, __read, __spreadArray } from "tslib";
import * as React from 'react';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import { useUpsaleWarning } from '../../../Checkout/components/Checkout/Upsale/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { getInsurances, getOrder, getOrderId, getServicesFallbackURL, shouldRedirectToFallback } from '../../../Checkout/store/order/selectors';
import Warning from '../../../Checkout/components/Checkout/Upsale/Warning/Warning';
import { useEffect, useMemo } from 'react';
import { fillOrder } from '../../../Checkout/store/order/actions';
import MediaQuery from '../../../MediaQuery/MediaQuery';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '../../../AdditionalServices/components/Insurance/DialogContent/DialogContent';
import { SlideBottom } from '../../../index';
import { ThemeProvider, useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import icons from '../../icons';
import { getPassengerState } from '../../../Checkout/store/passengers/selectors';
import WarningModal from '../../../WarningModal';
import DownloadInsurances from '../../../AdditionalServices/components/Insurance/DownloadInsurances/DownloadInsurances';
import { useInsurance } from './hooks';
import { useSaveOrderServicesMutation } from '@websky/graphql';
import { useRenderers } from '../../../renderProps';
import cn from 'classnames';
import { useConfig } from '../../../context';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import ServiceSummaryOption from '../../../ServiceSummary/components/ServiceSummaryOption';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import { getTotalPrice } from '../../../utils';
import InsurancePopup from '../../../InsurancePopup';
import Modal from '../../../Modal';
import { useIsMobile } from '../../../utils';
import Close from '../Close/Close';
var InsuranceAdditionalService = function (props) {
    var _a, _b, _c, _d, _e;
    // i18n & styles
    var t = useTranslation('Checkout').t;
    var cardStyles = useTheme('CheckoutAdditionalService').Insurance;
    var css = useTheme('AdditionalServices').Insurance;
    // hooks
    var insurances = useSelector(getInsurances);
    var selectedInsurances = React.useMemo(function () {
        var availablePrograms = new Set(props.insurancePrograms.map(function (program) { return program.code; }));
        return insurances.selectedInsurances.filter(function (selected) {
            return availablePrograms.has(selected.insuranceProgram.code);
        });
    }, [insurances]);
    var servicesRedirectURL = useSelector(getServicesFallbackURL);
    var order = useSelector(getOrder);
    var orderId = useSelector(getOrderId);
    var shouldRedirect = useSelector(shouldRedirectToFallback);
    var uniqueSelectedInsurancePrograms = useMemo(function () {
        var selectedInsuranceMap = new Map();
        selectedInsurances.forEach(function (insurance) {
            var code = insurance.insuranceProgram.code;
            if (!selectedInsuranceMap.has(code)) {
                selectedInsuranceMap.set(code, insurance);
            }
        });
        return __spreadArray([], __read(selectedInsuranceMap.values()), false);
    }, [selectedInsurances]);
    var totalPrice = useMemo(function () {
        var total = getTotalPrice(uniqueSelectedInsurancePrograms, function (insurance) { return insurance.passengerPrice; });
        total.amount = total.amount * order.travellers.length;
        return total;
    }, [selectedInsurances]);
    var passengers = useSelector(getPassengerState);
    var dispatch = useDispatch();
    // custom hooks
    var _f = useUpsaleWarning(), showWarning = _f.showWarning, closeWarning = _f.closeWarning, toggleWarningIfOrderConfirmed = _f.toggleWarningIfOrderConfirmed;
    var _g = __read(useSaveOrderServicesMutation(), 2), saveOrderServicesMutation = _g[0], _h = _g[1], data = _h.data, loading = _h.loading, error = _h.error;
    // computed variables
    var minPrice = useMemo(function () {
        if (props.insurancePrograms.length) {
            var min = props.insurancePrograms[0].totalPrice;
            for (var i = 1; i < props.insurancePrograms.length; i++) {
                if (props.insurancePrograms[i].totalPrice.amount < min.amount) {
                    min = props.insurancePrograms[i].totalPrice;
                }
            }
            return min;
        }
        return null;
    }, props.insurancePrograms);
    var isSelected = selectedInsurances.length > 0;
    var isMobile = useIsMobile();
    var _j = useInsurance({
        isSelected: isSelected,
        saveMutation: saveOrderServicesMutation,
        insurances: insurances,
        insurancePrograms: props.insurancePrograms,
        orderId: orderId,
        passengers: passengers
    }), 
    // data
    confirmationPopupIsOpen = _j.confirmationPopupIsOpen, downloadPopupOpen = _j.downloadPopupOpen, setDownloadPopupOpen = _j.setDownloadPopupOpen, insuranceRulesUrl = _j.insuranceRulesUrl, multiInsurance = _j.multiInsurance, 
    // insuranceDocuments,
    openMultiInsurance = _j.openMultiInsurance, setInsuranceVisible = _j.setInsuranceVisible, errors = _j.errors, setErrors = _j.setErrors, passengerDocuments = _j.passengerDocuments, 
    // functions
    saveInsurances = _j.saveInsurances, toggleInsurance = _j.toggleInsurance, closeConfirmationPopup = _j.closeConfirmationPopup, closeDownloadPopup = _j.closeDownloadPopup, onInsuranceRemove = _j.onInsuranceRemove, closeInsurance = _j.closeInsurance;
    useEffect(function () {
        var _a;
        if (loading === false && (data === null || data === void 0 ? void 0 : data.SaveOrderServices) && !((_a = error === null || error === void 0 ? void 0 : error.graphQLErrors) === null || _a === void 0 ? void 0 : _a.length)) {
            dispatch(fillOrder(data === null || data === void 0 ? void 0 : data.SaveOrderServices));
        }
    }, [loading, data, error]);
    var buttons = [];
    if ((_a = selectedInsurances[0]) === null || _a === void 0 ? void 0 : _a.policyUrl) {
        buttons.push({
            action: function () {
                if (selectedInsurances.length > 1) {
                    setDownloadPopupOpen(true);
                }
                else {
                    window.open(selectedInsurances[0].policyUrl, '_blank');
                }
            },
            text: t('Download')
        });
    }
    else {
        buttons.push({
            action: !shouldRedirect ? toggleInsurance : toggleWarningIfOrderConfirmed,
            text: t(multiInsurance ? 'Change' : 'Cancel'),
            isLoading: loading,
            buttonType: 'change'
        });
    }
    if (insuranceRulesUrl && !loading) {
        buttons.push({
            action: function () { return window.open(insuranceRulesUrl); },
            text: t('AdditionalServiceCard:Terms of service')
        });
    }
    var checkoutAdditionalServiceConfig = useConfig().global.checkoutAdditionalService;
    var CustomCheckoutAdditionalService = useRenderers().CheckoutAdditionalService;
    var ServiceWrapper = (_b = CustomCheckoutAdditionalService === null || CustomCheckoutAdditionalService === void 0 ? void 0 : CustomCheckoutAdditionalService.CheckoutAdditionalService) !== null && _b !== void 0 ? _b : CheckoutAdditionalService;
    var byCodeMap = (_d = (_c = checkoutAdditionalServiceConfig === null || checkoutAdditionalServiceConfig === void 0 ? void 0 : checkoutAdditionalServiceConfig.insurance) === null || _c === void 0 ? void 0 : _c.insuranceProgramCodeMap) === null || _d === void 0 ? void 0 : _d[props.insurancePrograms[0].code];
    var headerIcon = props.asSingle && (byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.headerIcon) ? byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.headerIcon : icons.insuranceIcon;
    var isSingleNotSelected = props.asSingle && !isSelected;
    var style = isSingleNotSelected && (byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.color) ? { background: byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.color } : undefined;
    var serviceWrapperEndAdornment = isSingleNotSelected && (byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.contentIcon) ? (React.createElement("div", { className: cardStyles.serviceWrapperEndAdornment }, byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.contentIcon)) : null;
    var header = props.asSingle && (byCodeMap === null || byCodeMap === void 0 ? void 0 : byCodeMap.header) ? t(byCodeMap.header) : t('Insurance');
    var renderCloseButton = function (onClick) { return React.createElement(Close, { onClick: onClick }); };
    var onInsuranceItemRemove = function (code) {
        saveInsurances(insurances.type, insurances.selectedInsurances
            .filter(function (selected) { return selected.insuranceProgram.code !== code; })
            .map(function (selected) { return selected.insuranceProgram; }));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(InsuranceSummary, { flight: order.flight, travellers: order.travellers, services: __assign(__assign({}, order.additionalServices), { insurances: __assign(__assign({}, order.additionalServices.insurances), { insurancePrograms: props.insurancePrograms }) }), onServiceDelete: function (_, payload) { return onInsuranceItemRemove(payload.code); } }, function (selectedInsurances) {
            var _a;
            var _b;
            return (React.createElement(ServiceWrapper, { style: style, icon: headerIcon, className: cn(cardStyles.insurance, (_a = {},
                    _a[cardStyles.withEndAdornment] = serviceWrapperEndAdornment,
                    _a)), isLoading: loading, addClick: !shouldRedirect ? toggleInsurance : toggleWarningIfOrderConfirmed, header: header, isSelected: !!selectedInsurances.length, priceFrom: minPrice, priceTotal: totalPrice, description: (_b = props.insurancePrograms[0]) === null || _b === void 0 ? void 0 : _b.description, buttons: buttons, endAdornment: serviceWrapperEndAdornment },
                React.createElement(ThemeProvider, { value: {
                        ServiceSummary: {
                            ServiceSummaryOption: {
                                option: cardStyles.selectedOption,
                                option__name: cardStyles.option__name
                            }
                        }
                    } },
                    React.createElement(ServiceSummaryDivider, null,
                        React.createElement(ServiceSummaryOption, { groupOption: {
                                items: selectedInsurances
                            } }))),
                selectedInsurances.length === 1 && (React.createElement("div", { dangerouslySetInnerHTML: { __html: selectedInsurances[0].description } }))));
        }),
        multiInsurance && (React.createElement(React.Fragment, null,
            React.createElement(Modal, { classes: {
                    scrollBody: css.scrollBody,
                    paper: css.paper
                }, open: openMultiInsurance, slideUp: isMobile, onClose: closeInsurance, closeButtonRenderer: renderCloseButton },
                React.createElement(InsurancePopup, { description: (_e = props.insurancePrograms[0]) === null || _e === void 0 ? void 0 : _e.description, insurances: props.insurancePrograms, selectedInsurances: uniqueSelectedInsurancePrograms, passengers: passengerDocuments, onClose: function () { return setInsuranceVisible(false); }, onSave: function (state) {
                        saveInsurances(insurances.type, state.selectedInsurances, state.passengers);
                    }, isLoading: loading })),
            React.createElement(WarningModal, { title: 'Some error', content: 'error', isOpen: errors, onClose: function () { return setErrors(false); }, onButtonClick: function () { return setErrors(false); } }))),
        React.createElement(MediaQuery, { minWidth: 'mobile' },
            React.createElement(Dialog, { open: confirmationPopupIsOpen, onClose: closeConfirmationPopup, classes: { paper: css.popup } },
                React.createElement(DialogContent, { closeConfirmationPopup: closeConfirmationPopup, onInsuranceRemove: onInsuranceRemove, refusingIsInProgress: loading })),
            React.createElement(Dialog, { open: downloadPopupOpen, onClose: closeDownloadPopup, classes: { paper: css.downloadPopup } },
                React.createElement(DownloadInsurances, { selectedInsurances: selectedInsurances }))),
        React.createElement(MediaQuery, { maxWidth: 'mobile' },
            React.createElement(SlideBottom, { onClose: closeConfirmationPopup, isOpen: confirmationPopupIsOpen },
                React.createElement("div", { className: css.slide__content },
                    React.createElement(DialogContent, { closeConfirmationPopup: closeConfirmationPopup, onInsuranceRemove: onInsuranceRemove, refusingIsInProgress: loading }))),
            React.createElement(SlideBottom, { isOpen: downloadPopupOpen, onClose: closeDownloadPopup },
                React.createElement(DownloadInsurances, { selectedInsurances: selectedInsurances }))),
        React.createElement(Warning, { open: showWarning, redirectURL: servicesRedirectURL, onClose: closeWarning })));
};
export default InsuranceAdditionalService;
